import { useMemo } from "react";
import type { CachedTopic } from "../slice";

/**
 * A node in the topic tree hierarchy.
 */
export type TopicTreeNode = {
  id: string;
  displayName: string;
  children: TopicTreeNode[];
  topics: CachedTopic[];
};

/**
 * Collection metadata fetched from API.
 */
export type CollectionInfo = {
  id: string;
  displayName: string;
  parentId: string | null;
};

/**
 * Builds a hierarchical tree structure from selected topics using their collectionTree arrays.
 *
 * The collectionTree array represents the path from root to the topic's parent collection.
 * For example: ["root", "technology", "software"] means the topic belongs to the "software"
 * collection which is under "technology" which is under "root".
 *
 * @param topics - Selected topics with their collectionTree arrays
 * @param collections - Collection metadata from API (id, displayName, parentId)
 * @returns Array of root nodes forming the topic tree
 */
export function useBuildTopicTree(
  topics: CachedTopic[],
  collections: CollectionInfo[]
): TopicTreeNode[] {
  return useMemo(() => {
    if (topics.length === 0) return [];

    // Build collection lookup map
    const collectionMap = new Map<string, CollectionInfo>();
    collections.forEach((c) => collectionMap.set(c.id, c));

    const expandToRootCache = new Map<string, string[]>();
    const expandToRoot = (leafId: string): string[] => {
      const cached = expandToRootCache.get(leafId);
      if (cached) return cached;
      const path: string[] = [];
      let currentId: string | null | undefined = leafId;
      while (currentId) {
        if (currentId === "0") break;
        path.push(currentId);
        const parentId: string | null | undefined =
          collectionMap.get(currentId)?.parentId;
        if (!parentId || parentId === "0") break;
        currentId = parentId;
      }
      expandToRootCache.set(leafId, path);
      return path;
    };

    // Extract unique collection IDs used by selected topics (including ancestors)
    const usedCollectionIds = new Set<string>();
    topics.forEach((topic) => {
      topic.collectionTree.forEach((collectionId) => {
        if (!collectionId || collectionId === "0") return;
        expandToRoot(collectionId).forEach((id) => usedCollectionIds.add(id));
      });
    });

    // Build tree nodes for each used collection
    const nodeMap = new Map<string, TopicTreeNode>();

    // Create nodes for all used collections
    usedCollectionIds.forEach((id) => {
      const info = collectionMap.get(id);
      nodeMap.set(id, {
        id,
        displayName: info?.displayName ?? id,
        children: [],
        topics: [],
      });
    });

    // Assign topics to all their parent collections (can appear in multiple)
    topics.forEach((topic) => {
      topic.collectionTree.forEach((parentId) => {
        const parentNode = nodeMap.get(parentId);
        if (parentNode) {
          if (!parentNode.topics.find((t) => t.id === topic.id)) {
            parentNode.topics.push(topic);
          }
        }
      });
    });

    // Build parent-child relationships
    usedCollectionIds.forEach((id) => {
      const info = collectionMap.get(id);
      if (info?.parentId && usedCollectionIds.has(info.parentId)) {
        const parentNode = nodeMap.get(info.parentId);
        const childNode = nodeMap.get(id);
        if (parentNode && childNode) {
          if (!parentNode.children.some((c) => c.id === childNode.id)) {
            parentNode.children.push(childNode);
          }
        }
      }
    });

    // Find root nodes (collections with no parent in our used set)
    const roots: TopicTreeNode[] = [];
    usedCollectionIds.forEach((id) => {
      const info = collectionMap.get(id);
      const node = nodeMap.get(id);
      if (node && (!info?.parentId || !usedCollectionIds.has(info.parentId))) {
        roots.push(node);
      }
    });

    // Sort children and topics alphabetically
    const sortNode = (node: TopicTreeNode): void => {
      node.children.sort((a, b) => a.displayName.localeCompare(b.displayName));
      node.topics.sort((a, b) => a.displayName.localeCompare(b.displayName));
      node.children.forEach(sortNode);
    };
    roots.forEach(sortNode);
    roots.sort((a, b) => a.displayName.localeCompare(b.displayName));

    return roots;
  }, [topics, collections]);
}

/**
 * Counts total topics in a tree node and its descendants.
 */
export function countTopicsInNode(node: TopicTreeNode): number {
  let count = node.topics.length;
  node.children.forEach((child) => {
    count += countTopicsInNode(child);
  });
  return count;
}
